import { ArrowRight } from "lucide-react";

/**
 * Reusable CTA banner.
 * Props:
 *  - onOpenEstimate (fn)
 *  - heading (string, required)
 *  - headingTag ('h2' | 'h3', default 'h2')
 *  - eyebrow (string, optional)
 *  - subheading (string, optional, shown below heading in muted color)
 *  - buttonLabel (string, default 'Request Your Free Estimate')
 */
export default function CTABanner({
  onOpenEstimate,
  heading = "Get a Free Estimate",
  headingTag = "h2",
  eyebrow = "Don't wait for the next storm",
  subheading = "Your roof protects everything. Make sure it's done right.",
  buttonLabel = "Request Your Free Estimate",
}) {
  const HeadingTag = headingTag;
  return (
    <section
      data-testid="cta-banner"
      className="relative bg-[var(--crest-amber)] text-[#121212] py-20 md:py-28 overflow-hidden"
    >
      {/* Texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.1' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
      <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#a05a05]/30 to-transparent pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#a05a05]/30 to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 flex flex-col lg:flex-row items-center justify-between gap-10">
        <div className="reveal flex-1">
          <span className="font-display uppercase tracking-[0.4em] text-xs font-bold text-[#121212]/70 mb-4 block">
            {eyebrow}
          </span>
          <HeadingTag
            data-testid="cta-banner-heading"
            className="font-display uppercase font-extrabold leading-[0.95] tracking-tight text-[#121212]"
            style={{ fontSize: "clamp(2rem, 4.8vw, 3.8rem)" }}
          >
            {heading}
          </HeadingTag>
          {subheading && (
            <p
              data-testid="cta-banner-subheading"
              className="font-display uppercase font-extrabold leading-[0.95] tracking-tight text-[#121212]/70 mt-2"
              style={{ fontSize: "clamp(1.4rem, 3vw, 2.4rem)" }}
            >
              {subheading}
            </p>
          )}
        </div>

        <button
          data-testid="cta-banner-button"
          onClick={onOpenEstimate}
          className="reveal group inline-flex items-center gap-3 font-display uppercase tracking-wider font-bold text-base md:text-lg px-9 py-5 bg-[#121212] text-white hover:bg-black transition-colors duration-300 whitespace-nowrap"
        >
          {buttonLabel}
          <ArrowRight
            size={20}
            className="transition-transform duration-300 group-hover:translate-x-1"
          />
        </button>
      </div>
    </section>
  );
}
